/**
 * PaywallProductService
 *
 * Manages PaywallProduct entities as a subcollection under projects.
 * Structure: projects/{projectId}/paywallProducts/{productId}
 *
 * PaywallProducts represent purchasable items configured with Stripe:
 * - One-time payments (single purchase)
 * - Subscriptions (recurring billing)
 * - Optional free or paid trials
 *
 * Operations:
 * - create: Create new paywall product with generated ID
 * - get: Get product by ID (returns null if not found)
 * - list: List all products for a project
 * - update: Update product using setDoc with merge
 * - delete: Delete product
 * - updatePreBakedPrice: Update pre-baked price info (at publish time)
 *
 * @module firebase-core/services
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import { paywallProductConverter } from '../converters/paywallProductConverter';
import type { PaywallProduct, PreBakedPriceInfo } from '@segmently/schema-models/payments';

/**
 * Input type for creating a PaywallProduct (id, createdAt, updatedAt auto-generated)
 */
export type CreatePaywallProductInput = Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>;

/**
 * Input type for updating a PaywallProduct
 */
export type UpdatePaywallProductInput = Partial<Omit<PaywallProduct, 'id' | 'createdAt'>>;

/**
 * Service for PaywallProduct CRUD operations.
 *
 * PaywallProducts are stored as: projects/{projectId}/paywallProducts/{productId}
 */
export class PaywallProductService {
  private readonly paywallProductsCollection = 'paywallProducts';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<PaywallProduct>;

  constructor(converter: FirestoreDataConverter<PaywallProduct> = paywallProductConverter) {
    this.converter = converter;
  }

  /**
   * Get collection reference for a project's paywall products
   */
  private getCollectionRef(projectId: string) {
    return collection(
      this.firestore,
      `projects/${projectId}/${this.paywallProductsCollection}`
    ).withConverter(this.converter);
  }

  /**
   * Get document reference for a specific paywall product
   */
  private getDocRef(projectId: string, productId: string) {
    return doc(
      this.firestore,
      `projects/${projectId}/${this.paywallProductsCollection}`,
      productId
    ).withConverter(this.converter);
  }

  // ============================================================================
  // CREATE
  // ============================================================================

  /**
   * Create a new paywall product with generated ID.
   *
   * @param projectId - Project ID
   * @param data - Paywall product data (id, createdAt, updatedAt auto-generated)
   * @returns Created paywall product with all fields populated
   */
  async create(projectId: string, data: CreatePaywallProductInput): Promise<PaywallProduct> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      const docRef = doc(collectionRef);
      const now = new Date();

      const productData: PaywallProduct = {
        ...data,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(docRef, productData);
      return productData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.create');
      throw error;
    }
  }

  // ============================================================================
  // READ
  // ============================================================================

  /**
   * Get paywall product by ID.
   *
   * @param projectId - Project ID
   * @param productId - Paywall product document ID
   * @returns PaywallProduct if found, null otherwise
   */
  async get(projectId: string, productId: string): Promise<PaywallProduct | null> {
    try {
      const docRef = this.getDocRef(projectId, productId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data();
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.get');
      throw error;
    }
  }

  /**
   * List all paywall products for a project.
   *
   * @param projectId - Project ID
   * @param options - Optional filters
   * @returns Array of paywall products
   */
  async list(
    projectId: string,
    options?: {
      type?: 'one_time' | 'subscription';
    }
  ): Promise<PaywallProduct[]> {
    try {
      const collectionRef = this.getCollectionRef(projectId);
      let q = query(collectionRef, orderBy('createdAt', 'desc'));

      if (options?.type) {
        q = query(q, where('type', '==', options.type));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((docSnap) => docSnap.data());
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.list');
      throw error;
    }
  }

  // ============================================================================
  // UPDATE
  // ============================================================================

  /**
   * Update paywall product fields.
   *
   * CRITICAL: Uses setDoc with merge (NEVER updateDoc) to ensure converter runs.
   *
   * @param projectId - Project ID
   * @param productId - Paywall product document ID
   * @param data - Partial paywall product data to update
   */
  async update(
    projectId: string,
    productId: string,
    data: UpdatePaywallProductInput
  ): Promise<void> {
    try {
      const docRef = this.getDocRef(projectId, productId);

      // CRITICAL: Use setDoc with merge, NEVER updateDoc
      await setDoc(
        docRef,
        {
          ...data,
          updatedAt: new Date(),
        } as Partial<PaywallProduct>,
        { merge: true }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.update');
      throw error;
    }
  }

  /**
   * Update pre-baked price info for a paywall product.
   *
   * Called at publish time to cache formatted price information.
   *
   * @param projectId - Project ID
   * @param productId - Paywall product document ID
   * @param preBakedPrice - Pre-baked price information
   */
  async updatePreBakedPrice(
    projectId: string,
    productId: string,
    preBakedPrice: PreBakedPriceInfo
  ): Promise<void> {
    try {
      await this.update(projectId, productId, {
        preBakedPrice: {
          ...preBakedPrice,
          bakedAt: new Date(),
        },
      });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(
        error,
        'PaywallProductService.updatePreBakedPrice'
      );
      throw error;
    }
  }

  // ============================================================================
  // DELETE
  // ============================================================================

  /**
   * Delete paywall product by ID.
   *
   * @param projectId - Project ID
   * @param productId - Paywall product document ID
   */
  async delete(projectId: string, productId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${projectId}/${this.paywallProductsCollection}`,
        productId
      );
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.delete');
      throw error;
    }
  }

  // ============================================================================
  // BATCH OPERATIONS
  // ============================================================================

  /**
   * Get multiple paywall products by IDs.
   *
   * @param projectId - Project ID
   * @param productIds - Array of product IDs to fetch
   * @returns Map of productId → PaywallProduct (missing IDs excluded)
   */
  async getMany(
    projectId: string,
    productIds: string[]
  ): Promise<Map<string, PaywallProduct>> {
    try {
      const result = new Map<string, PaywallProduct>();

      // Fetch in parallel
      const promises = productIds.map((id) => this.get(projectId, id));
      const products = await Promise.all(promises);

      products.forEach((product) => {
        if (product) {
          result.set(product.id, product);
        }
      });

      return result;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'PaywallProductService.getMany');
      throw error;
    }
  }
}
